import React, { useState, useEffect, useRef, useCallback } from "react";
import throttle from "lodash/throttle";
import { cls } from "cls";
import { NavBarItem, NavBarModel } from "../Models/PageCreatorPage";
import MenuIcon from "./MenuIcon";
import "../Styles/NavBar.less";

const NavBar = ({ navItems, backgroundColor, textColor }: NavBarModel) => {
  const [priorityItems, setPriorityItems] = useState<NavBarItem[]>([]);
  const [moreItems, setMoreItems] = useState<NavBarItem[]>([]);
  const [widthsArr, setWidthsArr] = useState<number[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const navigation = useRef<HTMLUListElement>(null);
  const navigationOuter = useRef<HTMLElement>(null);
  const moreMenu = useRef<HTMLUListElement>(null);
  const nav = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!navigation.current) return;
    const items = [...navigation.current.children];
    const itemsArr = items.map(
      (item) => item.getBoundingClientRect().width + 50,
    );
    setWidthsArr(itemsArr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation.current]);

  const updateNavigation = useCallback(() => {
    const outerWidth = navigationOuter?.current?.getBoundingClientRect().width;
    const moreMenuWidth = moreMenu.current
      ? moreMenu.current.getBoundingClientRect().width
      : 0;
    const arrayAmount = howManyItemsInMenuArray(
      widthsArr,
      outerWidth,
      moreMenuWidth,
      1,
    );
    const navItemsCopy = navItems;
    const priorityItems = navItemsCopy.slice(0, arrayAmount);
    setPriorityItems(priorityItems);
    setMoreItems(
      priorityItems.length !== navItemsCopy.length
        ? navItemsCopy.slice(arrayAmount, navItemsCopy.length)
        : [],
    );
  }, [navItems, widthsArr]);

  useEffect(() => {
    setPriorityItems(navItems);
  }, [navItems]);

  useEffect(() => {
    const handleResize = throttle(() => {
      updateNavigation();
    }, 150);

    window.addEventListener("resize", handleResize, true);
    updateNavigation();

    return () => {
      // cleanup event listener
      window.removeEventListener("resize", handleResize);
    };
  }, [updateNavigation]);

  const howManyItemsInMenuArray = (
    array: readonly number[],
    outerWidth: number | undefined,
    initialWidth: number,
    minimumNumberInNav: number,
  ) => {
    let total = initialWidth * 1.75;
    for (let i = 0; i < array.length; i += 1) {
      const number = array[i];
      if (number !== undefined && outerWidth) {
        if (total + number > outerWidth) {
          return i < minimumNumberInNav ? minimumNumberInNav : i;
        }
        total += number;
      }
    }

    return undefined;
  };

  const handleNavItemClick = () => {
    setShowMenu(false);
  };

  return (
    <div className="">
      <nav
        ref={navigationOuter}
        className={cls(
          "w-full",
          "flex",
          "justify-between",
          "sm:justify-center",
          "items-center",
        )}
        style={{ backgroundColor }}
      >
        <ul
          ref={navigation}
          className={cls(
            "flex",
            "h-12",
            "mb-0",
            "box-border",
            "items-center",
            "ml-0",
          )}
        >
          {priorityItems.map((item) => (
            <li
              className={`menu-items ${cls(
                "flex-shrink-0",
                "list-none",
                "self-center",
                "text-sm",
                "px-4",
              )}`}
              key={item.title}
              onClick={handleNavItemClick}
            >
              <a
                style={{ color: textColor }}
                href={item.url}
                className={cls(
                  "text-sm",
                  "font-semibold",
                  "hover:text-gray-200",
                  "hover:underline",
                  "transition",
                  "duration-150",
                  "ease-in",
                )}
              >
                {item.title}
              </a>
            </li>
          ))}
        </ul>
        {moreItems.length > 0 && (
          <ul
            ref={moreMenu}
            className={cls(
              "flex",
              "h-12",
              "mb-0",
              "justify-between",
              "items-center",
            )}
          >
            <li
              className={`more-item ${cls("relative", "list-none", "h-full")}`}
            >
              <button
                type="button"
                onClick={() => setShowMenu((prev) => !prev)}
                id="lastItem"
                className={`${cls(
                  "bg-transparent",
                  "border-none",
                  "h-full",
                  "text-sm",
                  "md:text-base",
                  "font-semibold",
                  "flex",
                  "items-center",
                  "px-4",
                  "mr-4",
                  "hover:backdrop-brightness-75",
                )}
                  active:backdrop-brightness-50
                `}
                style={{
                  backdropFilter: showMenu ? "brightness(.5)" : "",
                }}
              >
                <div
                  className={cls("xsl:hidden")}
                  style={{
                    color: showMenu ? "#fff" : textColor,
                  }}
                >
                  <MenuIcon />
                </div>
                <div
                  className={cls("hidden", "text-sm", "xsl:block")}
                  style={{
                    color: showMenu ? "#fff" : textColor,
                  }}
                >
                  More
                  <svg
                    className="ml-2"
                    width="12"
                    height="10"
                    viewBox="0 0 13 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      className={cls("fill-current")}
                      d="M0 -5.68248e-07L-8.88614e-08 2.03291L6.5 7L13 2.03291L13 0L6.5 4.98987L0 -5.68248e-07Z"
                    />
                  </svg>
                </div>
              </button>
              <ul
                ref={nav}
                className={`absolute right-4 list-none bg-white z-30 w-[calc(100vw-3rem)] sm:w-fit sm:min-w-[192px] m-0 text-left ${
                  showMenu ? "block" : "hidden"
                }`}
                style={{
                  borderRight: `4px solid ${backgroundColor}`,
                  boxShadow:
                    "0px 4px 6px -4px rgba(0, 0, 0, 0.48), 0px 10px 15px -3px rgba(0, 0, 0, 0.48)",
                }}
              >
                {moreItems.map((item) => (
                  <li
                    key={item.title}
                    className={`navigation-item ${cls("relative")}`}
                    onClick={handleNavItemClick}
                  >
                    <a
                      href={item.url}
                      className={cls("text-sm", "font-semibold", "block")}
                      style={{ color: "#0a0a0a" }}
                    >
                      {item.title}
                    </a>
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        )}
      </nav>
    </div>
  );
};

export default NavBar;
